import { Injectable, computed, signal } from '@angular/core';
import type { ContractActivity, ActivityDetails } from '../models/contract-activity.model';
import { ContractActionType } from '../models/contract-activity.model';
import type { IContractActivitiesStore } from './contract-activities-store.interface';

@Injectable({ providedIn: 'root' })
export class ContractActivitiesMockStore implements IContractActivitiesStore {
  private readonly _contractUID = signal<string | null>(null);
  private readonly _activitiesByContract = signal<ReadonlyMap<string, readonly ContractActivity[]>>(new Map());
  private readonly _loading = signal(false);
  private readonly _lastError = signal<Error | null>(null);

  readonly contractUID = this._contractUID.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly lastError = this._lastError.asReadonly();

  readonly activities = computed(() => {
    const cid = this._contractUID();
    if (!cid) return [];
    return this._activitiesByContract().get(cid) ?? [];
  });

  readonly totalCount = computed(() => this.activities().length);

  async loadForContract(contractUID: string): Promise<void> {
    if (this._contractUID() === contractUID) return;
    this._contractUID.set(contractUID);
    this._lastError.set(null);
  }

  async refresh(): Promise<void> {
    this._loading.set(true);
    await Promise.resolve();
    this._loading.set(false);
  }

  async logActivity(
    contractUID: string,
    action: ContractActionType,
    description: string,
    details?: ActivityDetails,
    actionDate?: Date
  ): Promise<string> {
    const activityUID = `mock-act-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
    const entry: ContractActivity = {
      activityUID,
      contractUID,
      action,
      description,
      date: actionDate ?? new Date(),
      createdAt: new Date(),
      details
    } as ContractActivity;
    this.mutate(contractUID, list => [entry, ...list]);
    return activityUID;
  }

  async logIndexationNotification(
    contractUID: string,
    _indexationType: string,
    _baseIndexValue: number,
    _newIndexValue: number,
    _previousRentAmount: number,
    _newRentAmount: number,
    _effectiveDate: Date,
    _indexationCap?: number,
    _notes?: string
  ): Promise<string> {
    return this.logActivity(contractUID, ContractActionType.INDEXATION_NOTIFICATION_SENT, 'Indexation notification sent');
  }

  async logIndexationApplied(
    contractUID: string,
    _indexationType: string,
    _indexationRate: number,
    _previousRentAmount: number,
    _newRentAmount: number,
    _effectiveDate: Date
  ): Promise<string> {
    return this.logActivity(contractUID, ContractActionType.INDEXATION_APPLIED, 'Indexation applied');
  }

  async logTerminationNotification(
    contractUID: string,
    _terminationDate: Date,
    _reason?: string,
    _noticeGiven?: Date
  ): Promise<string> {
    return this.logActivity(contractUID, ContractActionType.TERMINATION_NOTIFICATION_SENT, 'Termination notification sent');
  }

  getActivityStats(): Readonly<Record<string, number>> {
    return this.activities().reduce<Record<string, number>>((acc, a) => {
      const key = a.action as string;
      acc[key] = (acc[key] || 0) + 1;
      return acc;
    }, {});
  }

  getActivityCount(): number {
    return this.totalCount();
  }

  private mutate(contractUID: string, fn: (list: readonly ContractActivity[]) => readonly ContractActivity[]): void {
    const map = new Map(this._activitiesByContract());
    map.set(contractUID, fn(map.get(contractUID) ?? []));
    this._activitiesByContract.set(map);
  }
}
